import React from "react";
import classNames from "classnames";
import { getProfileURL } from "../utils/profile-url";
import Button from "./Button";
import { GuildPlayerState } from "../pages/Guild/data";
import { GuildPlayerStateEnum } from "../pages/Guild/types";
import truncateString from "../utils/truncate-string";
import { formatNumber } from "../utils/format-number";
import { Link } from "react-router-dom";
import TimeAgo from "javascript-time-ago";
import { useAppTranslation } from "../i18n/useAppTranslation";

interface GuildPlayer {
  joinDate: string;
  state: number;
  playerId: number;
  username: string;
  score: number;
  photoFile: string;
  rank: number;
}

type Props = {
  guildId: number;
  isOwner?: boolean;
  playerRole: GuildPlayerStateEnum;
  /** False while the guild is full: a request waits until a seat is free. */
  canAccept?: boolean;
  onChangeRole: (state: number, id: number) => void;
  onKick: (id: number) => void;
  onAccept: (id: number) => void;
  onReject: (id: number) => void;
};

// The still troop icons. The matching /troops/image files are animated and about 2 MB each.
const roleData = {
  [GuildPlayerStateEnum.Owner]: {
    image: "https://core.chainoflegends.com/assets/troops/icon/knight.png",
    titleKey: "guild:detail.memberRoles.lord",
  },
  [GuildPlayerStateEnum.Admin]: {
    image: "https://core.chainoflegends.com/assets/troops/icon/mage.png",
    titleKey: "guild:detail.memberRoles.commander",
  },
  [GuildPlayerStateEnum.Member]: {
    image: "https://core.chainoflegends.com/assets/troops/icon/warrior.png",
    titleKey: "guild:detail.memberRoles.sergeant",
  },
};

const GuildMemberBox = ({
  guildId,
  playerRole,
  canAccept = true,
  onChangeRole,
  onAccept,
  onReject,
  ...props
}: Props & GuildPlayer) => {
  const { t } = useAppTranslation(["guild", "common", "battle", "leaderboard"]);
  const role = roleData[props.state];
  const timeAgo = new TimeAgo("en");
  const joined = props.joinDate ? new Date(props.joinDate) : null;
  const photo = getProfileURL(props.playerId, props.photoFile);
  const viewerCommands = Boolean(playerRole) && playerRole < GuildPlayerStateEnum.Member;
  const canManage =
    viewerCommands &&
    props.state <= GuildPlayerStateEnum.Member &&
    props.state !== GuildPlayerStateEnum.Owner;
  const canAnswer = viewerCommands && props.state === GuildPlayerStateEnum.JoinRequest;

  return (
    <article
      className={classNames("gh-member", {
        "is-lord": props.state === GuildPlayerStateEnum.Owner,
        "is-waiting": props.state > GuildPlayerStateEnum.Member,
      })}
    >
      <Link to={"/profile/" + props.playerId} className="gh-member-avatar" aria-label={props.username}>
        {photo && <img src={photo} alt="" />}
      </Link>
      <div style={{ minWidth: 0 }}>
        <Link to={"/profile/" + props.playerId} className="gh-member-name">
          {props.username?.startsWith("0x") ? truncateString(props.username) : props.username}
        </Link>
        {/* The roster is already grouped by role, so the line under the name says how long they have been in. */}
        <div className="gh-member-role">
          {role
            ? joined && !isNaN(joined.getTime())
              ? `Joined ${timeAgo.format(joined)}`
              : t(role.titleKey)
            : t(`guild:detail.playerStates.${props.state}`, {
                defaultValue: GuildPlayerState[props.state],
              })}
        </div>
        <div className="gh-member-figures">
          <span>
            {t("leaderboard:columns.rank")} <b>#{props.rank}</b>
          </span>
          <span>
            {t("guild:detail.stats.worth")} <b>{formatNumber(props.score)}</b>
          </span>
        </div>
      </div>
      {role ? <img className="gh-member-emblem" src={role.image} alt="" /> : <span />}
      {(canManage || canAnswer) && (
        <div className="gh-member-actions">
          {canManage && props.isOwner && (
            <Button onClick={() => onChangeRole(props.state, props.playerId)} size="small" variant="primary">
              {t("guild:detail.dialogs.changeRole.title")}
            </Button>
          )}
          {canManage && (
            <Button onClick={() => props.onKick(props.playerId)} size="small" variant="secondary">
              {t("common:actions.remove")}
            </Button>
          )}
          {canAnswer && (
            <Button onClick={() => onAccept(props.playerId)} size="small" variant="primary" disabled={!canAccept}>
              {t("battle:matchSuggestion.accept")}
            </Button>
          )}
          {canAnswer && (
            <Button onClick={() => onReject(props.playerId)} size="small" variant="secondary">
              {t("battle:matchSuggestion.reject")}
            </Button>
          )}
        </div>
      )}
    </article>
  );
};

export default GuildMemberBox;
